/* FC001P-13 Recipes listing: filter drawer, counts and results.

   Two things in Figma 3101:4137 cannot be built from Liquid on a blog — a count
   on every option, and checkboxes that behave like checkboxes:

     - `blog.all_tags` carries no counts and `blog.articles` is capped per page,
       so no template can tally 268 articles.
     - /blogs/x/tagged/a+b ANDs its tags. Only 9 of 356 recipes carry two flavour
       tags, so 94% of flavour pairs, 86% of spirit pairs and 74% of spirit x
       flavour pairs return nothing. AND is the wrong operator for a group of
       checkboxes on this data.

   So a precomputed article -> tag index (assets/th-recipe-index.json, built by
   analysis/build_recipe_index.py) drives the drawer: OR within a group, AND
   across groups, and counts derived against the OTHER groups' selections so an
   option never advertises a combination that returns nothing.

   Degradation is deliberate. No index, or a fetch failure, and the drawer falls
   back to the /tagged/ navigation it used before; the unfiltered listing is
   always server-rendered, so the page is never blank and never depends on JS. */
(function () {
  var indexCache = null;

  function openPanel(section) {
    var panel = section.querySelector('[data-filters-panel]');
    if (!panel) return;
    panel.hidden = false;
    document.body.style.overflow = 'hidden';
    var toggle = section.querySelector('[data-filters-open]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    var close = panel.querySelector('.th-rfilters__close');
    if (close) close.focus();
  }

  function closePanel(section) {
    var panel = section.querySelector('[data-filters-panel]');
    if (!panel) return;
    panel.hidden = true;
    document.body.style.overflow = '';
    var toggle = section.querySelector('[data-filters-open]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }
  }

  /* ---------- index ---------- */

  function loadIndex(section) {
    if (indexCache) return indexCache;
    var url = section.getAttribute('data-index-url');
    if (!url) return null;
    indexCache = fetch(url)
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (data) {
        // Normalise once so the hot paths only ever touch Sets.
        (data.articles || []).forEach(function (a) { a.set = new Set(a.g || []); });
        return data;
      })
      .catch(function () { return null; });
    return indexCache;
  }

  function groupsOf(section) {
    return [].slice.call(section.querySelectorAll('[data-filter-group]')).map(function (el) {
      return {
        el: el,
        key: el.getAttribute('data-group-prefix') || '',
        inputs: [].slice.call(el.querySelectorAll('.th-rfilters__checkbox'))
      };
    }).filter(function (g) { return g.key; });
  }

  /* Selection lives in the query string so reload, deep link and browser back
     all work — the /tagged/ path cannot express OR, which is the whole point. */
  function selectionFromUrl(groups) {
    var params = new URLSearchParams(window.location.search);
    var sel = {};
    groups.forEach(function (g) {
      var raw = params.get(g.key);
      sel[g.key] = raw ? raw.split(',').filter(Boolean) : [];
    });
    // A /tagged/ URL (a quick-filter pill, or a link from before this shipped)
    // still has to light up the drawer and feed the counts.
    var m = window.location.pathname.match(/\/tagged\/([^/?#]+)/);
    if (m && !params.toString()) {
      decodeURIComponent(m[1]).split('+').forEach(function (tag) {
        groups.forEach(function (g) {
          if (tag.indexOf(g.key + '-') === 0 && sel[g.key].indexOf(tag) === -1) sel[g.key].push(tag);
        });
      });
    }
    return sel;
  }

  function selectionFromDom(groups) {
    var sel = {};
    groups.forEach(function (g) {
      sel[g.key] = g.inputs.filter(function (i) { return i.checked; }).map(function (i) { return i.value; });
    });
    return sel;
  }

  function isEmpty(sel) {
    return Object.keys(sel).every(function (k) { return !sel[k].length; });
  }

  /* OR inside a group, AND across groups. `skip` drops one group so a count can
     be measured against the other facets only. */
  function match(article, sel, skip) {
    for (var key in sel) {
      if (key === skip) continue;
      var picks = sel[key];
      if (!picks.length) continue;
      var hit = false;
      for (var i = 0; i < picks.length; i++) {
        if (article.set.has(picks[i])) { hit = true; break; }
      }
      if (!hit) return false;
    }
    return true;
  }

  function filter(data, sel) {
    return (data.articles || []).filter(function (a) { return match(a, sel, null); });
  }

  /* A count is measured against every OTHER group's selection, never against the
     whole blog and never against its own group. Counting against the whole blog
     would advertise combinations that return nothing — the defect this replaces.
     Counting against its own group would zero every unticked sibling. */
  function renderCounts(section, data, sel) {
    groupsOf(section).forEach(function (g) {
      var pool = (data.articles || []).filter(function (a) { return match(a, sel, g.key); });
      var visible = 0;
      g.inputs.forEach(function (input) {
        var n = 0;
        for (var i = 0; i < pool.length; i++) if (pool[i].set.has(input.value)) n++;
        var row = input.closest('.th-rfilters__row');
        var slot = row && row.querySelector('[data-row-count]');

        /* A "(0)" option is a dead end by construction — the count already
           accounts for every other group, so ticking it can only empty the
           grid. Victoria's QA item 9, 2026-08-19: hide them rather than offer
           them.

           Unticking has to stay possible, so a CHECKED row is never hidden. Its
           own group is skipped when its count is measured, so a sibling group's
           selection can legitimately take a ticked option to zero — tequila +
           bourbon + berry puts bourbon at 0 — and hiding it there would strand
           the user with a filter they cannot remove.

           That row keeps its tick and LOSES its count instead. Jose's call,
           2026-08-20, after the bourbon report: no option anywhere in the
           drawer ever shows a zero, which is the QA ask read literally, and the
           filter stays removable. `.th-rfilters__row-count:empty` is already
           `display: none`, so an empty string is enough — the parentheses must
           not be written and then hidden, or the label's text run carries a
           stray space.

           Nothing hides until the index resolves. On the fallback path
           renderCounts never runs, so every row stays visible, every count
           stays empty and the /tagged/ navigation is unchanged. */
        var dead = n === 0 && !input.checked;
        if (slot) slot.textContent = n === 0 && input.checked ? '' : '(' + n + ')';
        if (row) row.hidden = dead;
        if (!dead) visible++;
      });

      /* All rows gone leaves a header over nothing — reachable on this data:
         sp-mezcal carries no fl: tag at all, so picking it zeroes all 16
         Flavour options. A group cannot vanish while it holds a tick (above),
         and a group with no tick anywhere in the drawer counts against the
         whole blog, so the drawer can never empty completely. */
      g.el.hidden = visible === 0;
    });
  }

  /* Mirrors snippets/article-recipe-card.liquid. Only the nodes the listing
     actually paints are built: `.card__time-read` and `.card__badge` are
     display:none in this grid, and the hover-metafield image is suppressed, so
     emitting them would add weight nothing renders. */
  function cardHtml(article, blogUrl) {
    var url = blogUrl + '/' + article.h;
    var img = article.i
      ? '<img src="' + article.i + '" alt="" class="motion-reduce article-recipe-card__image" loading="lazy">'
      : '';
    var sub = article.s ? '<div class="card__subtitle">' + article.s + '</div>' : '';
    return '<div class="th-rgrid__item">' +
      '<a href="' + url + '" class="card-wrapper underline-links-hover">' +
        '<div class="article-recipe-card__image-wrapper">' + img + '</div>' +
        '<div class="card__content"><div class="card__information">' +
          '<h4 class="card__heading">' + article.t + '</h4>' + sub +
        '</div></div>' +
      '</a></div>';
  }

  function paint(section, state) {
    var grid = section.querySelector('[data-recipes-grid]');
    var slice = state.results.slice(0, state.shown);
    grid.innerHTML = slice.map(function (a) { return cardHtml(a, state.blogUrl); }).join('');

    var countEl = section.querySelector('.th-rgrid__count');
    if (countEl) countEl.textContent = state.results.length + ' ' + state.countLabel;

    var empty = section.querySelector('.th-rgrid__empty');
    if (empty) empty.hidden = state.results.length > 0;

    var wrap = section.querySelector('[data-load-more-wrap]');
    if (wrap) wrap.hidden = state.shown >= state.results.length;
  }

  function applyState(section, data, sel, push) {
    var state = section.thState;
    state.results = filter(data, sel);
    state.shown = Math.min(state.perPage, state.results.length);
    paint(section, state);
    renderCounts(section, data, sel);

    if (push) {
      // Built by hand rather than with URLSearchParams.toString(), which
      // percent-encodes the separator to ?sp=sp-gin%2Csp-rum. A comma is a legal
      // sub-delim in a query value and the URL gets shared and pasted into
      // tickets, so it stays readable.
      var qs = Object.keys(sel)
        .filter(function (k) { return sel[k].length; })
        .map(function (k) { return encodeURIComponent(k) + '=' + sel[k].map(encodeURIComponent).join(','); })
        .join('&');
      history.pushState({ th: sel }, '', qs ? state.blogUrl + '?' + qs : state.blogUrl);
    }
  }

  function syncInputs(groups, sel) {
    groups.forEach(function (g) {
      g.inputs.forEach(function (i) { i.checked = (sel[g.key] || []).indexOf(i.value) !== -1; });
    });
  }

  /* ---------- server-side load more (search results, and the no-JS-index path) ---------- */

  function loadMoreRemote(section, button) {
    var url = button.getAttribute('data-next-url');
    var grid = section.querySelector('[data-recipes-grid]');
    if (!url || !grid || button.hasAttribute('aria-busy')) return;

    button.setAttribute('aria-busy', 'true');
    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var items = parsed.querySelectorAll('[data-recipes-grid] .th-rgrid__item');
        [].forEach.call(items, function (item) { grid.appendChild(item); });

        var next = parsed.querySelector('[data-load-more]');
        if (next) {
          button.setAttribute('data-next-url', next.getAttribute('data-next-url'));
          button.removeAttribute('aria-busy');
        } else {
          var wrap = section.querySelector('[data-load-more-wrap]');
          if (wrap) wrap.remove();
        }
      })
      .catch(function () { button.removeAttribute('aria-busy'); });
  }

  /* ---------- wiring ---------- */

  function bind(section) {
    if (section.dataset.thRecipesBound) return;
    section.dataset.thRecipesBound = 'true';

    var open = section.querySelector('[data-filters-open]');
    if (open) open.addEventListener('click', function () { openPanel(section); });

    section.querySelectorAll('[data-filters-close]').forEach(function (el) {
      el.addEventListener('click', function () { closePanel(section); });
    });

    section.querySelectorAll('[data-group-toggle]').forEach(function (head) {
      head.addEventListener('click', function () {
        var group = head.closest('[data-filter-group]');
        if (!group) return;
        var isOpen = group.classList.toggle('th-rfilters__group--open');
        head.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var panel = section.querySelector('[data-filters-panel]');
      if (panel && !panel.hidden) closePanel(section);
    });

    var panel = section.querySelector('[data-filters-panel]');
    var apply = section.querySelector('[data-filters-apply]');
    var more = section.querySelector('[data-load-more]');
    var groups = groupsOf(section);

    section.thState = {
      blogUrl: (apply && apply.getAttribute('data-blog-url')) || '/blogs/recipes',
      perPage: parseInt(section.getAttribute('data-per-page'), 10) || 16,
      countLabel: section.getAttribute('data-count-label') || 'recipes',
      results: null,
      shown: 0
    };

    // Search results and any index-less listing keep the server pagination.
    if (more) {
      more.addEventListener('click', function () {
        var state = section.thState;
        if (state.results) {
          state.shown = Math.min(state.shown + state.perPage, state.results.length);
          paint(section, state);
        } else {
          loadMoreRemote(section, more);
        }
      });
    }

    if (!panel || !groups.length) return;

    var pending = loadIndex(section);
    if (!pending) {
      // No index asset: keep the pre-existing /tagged/ behaviour verbatim.
      if (apply) apply.addEventListener('click', function () { legacyApply(section, panel, apply); });
      return;
    }

    pending.then(function (data) {
      if (!data) {
        if (apply) apply.addEventListener('click', function () { legacyApply(section, panel, apply); });
        return;
      }

      var urlSel = selectionFromUrl(groups);
      syncInputs(groups, urlSel);
      if (isEmpty(urlSel)) {
        // Nothing selected: leave the server-rendered markup exactly as it is,
        // and only fill the counts in.
        renderCounts(section, data, urlSel);
      } else {
        applyState(section, data, urlSel, false);
      }

      if (apply) {
        apply.addEventListener('click', function () {
          applyState(section, data, selectionFromDom(groups), true);
          closePanel(section);
        });
      }

      // Live counts while ticking, so the effect of a choice is visible before
      // Apply is pressed.
      groups.forEach(function (g) {
        g.inputs.forEach(function (i) {
          i.addEventListener('change', function () { renderCounts(section, data, selectionFromDom(groups)); });
        });
      });

      var clear = section.querySelector('.th-rfilters__clear');
      if (clear) {
        clear.addEventListener('click', function (e) {
          e.preventDefault();
          var empty = {};
          groups.forEach(function (g) { empty[g.key] = []; });
          syncInputs(groups, empty);
          applyState(section, data, empty, true);
          closePanel(section);
        });
      }

      window.addEventListener('popstate', function () {
        var sel = selectionFromUrl(groups);
        syncInputs(groups, sel);
        applyState(section, data, sel, false);
      });
    });
  }

  function legacyApply(section, panel, apply) {
    var base = apply.getAttribute('data-blog-url') || '/';
    var picked = [].slice
      .call(panel.querySelectorAll('.th-rfilters__checkbox:checked'))
      .map(function (input) { return input.value; });
    window.location.href = picked.length ? base + '/tagged/' + picked.join('+') : base;
  }

  function init() {
    document.querySelectorAll('.th-rgrid').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
